/**
 * #981 stage 2 — the large-CSV grid's editing glue (kept out of LargeCsvGrid.tsx): what an edit in
 * AG Grid does (a cell → `setCell` on the row's identity; a row / column op), what a refused edit
 * says, the grid refresh when the docs change, "Saving…" stopping an open cell editor, the stale
 * view after an edit under a sort / filter, and the two "save as a copy" paths (a conflict; the
 * stale edits of an earlier content).
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import { useTranslation } from '@papan/i18n'
import { useToastOptional } from '@papan/core-ui/ui'
import type { CsvEditResult, CsvRowTarget, LargeCsvSessionApi } from '../../client/useLargeCsvSession.js'
import type { LargeCsvOpenReady } from '../../client/large-csv-adapter.js'
import { copyName, saveLargeCsvCopy, type SaveCopyResult } from './save-copy.js'

type Row = { row: number; fields: string[]; base?: string | null; deleted?: true } & Partial<CsvRowTarget>

/** Debounce of a grid refresh after the docs changed (remote edits, a structural change). */
const REFRESH_MS = 150

/**
 * The row was read at `base` (the session's content hash). A row still held from an older content — a save
 * switched the session to the new one in place — names that content's fragment rows: an edit through it is
 * refused, never re-targeted (#981 no-reload save).
 */
export function rowIsCurrent<T extends { base?: string | null }>(data: T | undefined, base: string): boolean {
  return !!data && !!data.base && data.base === base
}

export function targetOf(data: Row | undefined): CsvRowTarget | null {
  if (!data || typeof data.fragId !== 'string' || typeof data.src !== 'number') return null
  return { fragId: data.fragId, src: data.src, ...(data.item ? { item: data.item } : {}) }
}

export function useLargeCsvEditing(
  gridRef: RefObject<AgGridReact<Row> | null>,
  session: LargeCsvSessionApi | null,
  fileId: string,
  fileName: string | undefined,
  parentId: string | null,
  summary: LargeCsvOpenReady,
  /** The table is switching content / session in place (a save, a Discard, a failed save): no edits. */
  swapping = false,
) {
  const { t } = useTranslation('collab')
  const toast = useToastOptional()
  const sessionRef = useRef(session)
  sessionRef.current = session
  const [message, setMessage] = useState<string | null>(null)
  const [viewActive, setViewActive] = useState(false)
  const viewActiveRef = useRef(false)
  viewActiveRef.current = viewActive
  const [viewStale, setViewStale] = useState(false)
  /**
   * #981 Task 9: the grid shows the OLD order over the saved rows (a save that moved no row) while the new view is
   * built — said by the same "not re-sorted" notice; it goes when a real view (or none) is shown.
   */
  const [provisional, setProvisional] = useState(false)
  /** The swap ends on the old order: the notice says so (an edit's stale state before the save is covered by the save). */
  const showProvisionalView = useCallback(() => { setViewStale(false); setProvisional(true) }, [])
  /** What the latest read showed: the old order (true) or a real view / none (false). */
  const setProvisionalView = useCallback((on: boolean) => setProvisional(on), [])

  /**
   * Cells take edits only while the session is ready, not saving, still open, not being swapped — and while its writes
   * are not refused here (#1315 A: its access dropped, a delete holds the room; the banner says why).
   */
  const canEdit = !!session && session.ready && !session.saving && !session.ended && !session.writesRefusedHere && !swapping
  /**
   * Save, Discard and Save as a copy — the edits kept so far. Also once a fragment was refused as too large to edit
   * here (#981 Task 13): editing stops, but those work on the server without that fragment's room, so unsaved edits
   * elsewhere in the file are never stranded.
   */
  const canSave = !!session && session.ready && !session.saving && (!session.ended || session.ended === 'too_large') && !swapping
  const canEditRef = useRef(canEdit)
  canEditRef.current = canEdit
  /** The content the session edits: a row read at another refuses the edit. */
  const baseRef = useRef(summary.contentHash)
  baseRef.current = summary.contentHash

  const say = useCallback((text: string) => {
    setMessage(text)
    toast?.error(text)
  }, [toast])

  const onResult = useCallback((r: CsvEditResult, op?: 'delete') => {
    if (r === 'ok') return
    // #1315 A: writes refused here — the banner explains it; just show the truth again
    if (r === 'read_only') { gridRef.current?.api?.refreshInfiniteCache(); return }
    // the session ended meanwhile (a fragment too large to edit here, …): say that — "try again" would be untrue
    const ended = sessionRef.current?.ended
    say(ended ? t(`largeCsv.edit.ended.${ended}`)
      : r === 'row_deleted' ? t('largeCsv.edit.rowDeleted')
      : r === 'saving' ? t('largeCsv.edit.refusedWhileSaving')
      // a delete is shown done at once: when it can't be applied, say plainly that the row is still there
      : op === 'delete' ? t('largeCsv.edit.deleteNotReady')
      : t('largeCsv.edit.notReady'))
    // show the truth again
    gridRef.current?.api?.refreshInfiniteCache()
  }, [say, t, gridRef])

  const touched = useCallback(() => {
    setMessage(null)
    if (viewActiveRef.current) setViewStale(true)
  }, [])

  /**
   * AG Grid's `valueSetter` for the column `slot` (its colId), shown at display index `i`. The edit names the SLOT:
   * a peer's column change while this editor was open shifts display indexes, never slots.
   */
  const setCellValue = useCallback((data: Row | undefined, i: number, slot: string, raw: unknown): boolean => {
    const s = sessionRef.current
    const target = targetOf(data)
    if (!s || !target || !data || !canEditRef.current || data.deleted || !rowIsCurrent(data, baseRef.current)) return false
    const v = raw == null ? '' : String(raw)
    if (v === (data.fields[i] ?? '')) return false
    data.fields[i] = v
    touched()
    void s.setCell(target, slot, v).then(onResult)
    return true
  }, [onResult, touched])

  /** A row from an older content (or editing paused): said, and the rows re-read — nothing is applied. */
  const staleRow = useCallback((data: Row | undefined): boolean => {
    if (canEditRef.current && rowIsCurrent(data, baseRef.current)) return false
    onResult('not_ready')
    return true
  }, [onResult])

  const insertRows = useCallback((data: Row | undefined, where: 'above' | 'below') => {
    const s = sessionRef.current
    const target = targetOf(data)
    if (!s || !target || data?.deleted || staleRow(data)) return
    touched()
    void s.insertRows(target, 1, where).then(onResult)
  }, [onResult, touched, staleRow])

  /**
   * #981 Task 11 — the row is shown deleted AT ONCE (its node redrawn), not once its fragment room has arrived and the
   * block was read again (10–12 s on stagetest, QA D2). The session marks it pending too, so a read meanwhile keeps the
   * mark; a delete that can't be applied takes it off again and says so.
   */
  const markDeleted = useCallback((target: CsvRowTarget, row: number, on: boolean) => {
    const api = gridRef.current?.api
    const node = api?.getRowNode(String(row))
    const d = node?.data as Row | undefined
    if (!api || !node || !d || d.fragId !== target.fragId || d.src !== target.src) return
    if (on) d.deleted = true
    else delete d.deleted
    api.redrawRows({ rowNodes: [node] })
  }, [gridRef])

  const deleteRow = useCallback((data: Row | undefined) => {
    const s = sessionRef.current
    const target = targetOf(data)
    if (!s || !target || !data || data.deleted || staleRow(data)) return
    touched()
    markDeleted(target, data.row, true)
    void s.deleteRows([target]).then((r) => {
      if (r !== 'ok' && r !== 'row_deleted') markDeleted(target, data.row, false)
      onResult(r, 'delete')
    }, () => {
      // never left shown deleted when the delete threw
      markDeleted(target, data.row, false)
      onResult('not_ready', 'delete')
    })
  }, [onResult, touched, staleRow, markDeleted])

  const columnOp = useCallback((r: CsvEditResult) => { touched(); onResult(r) }, [onResult, touched])

  const refreshView = useCallback(() => {
    setViewStale(false)
    setProvisional(false)
    gridRef.current?.api?.refreshInfiniteCache()
  }, [gridRef])

  // The docs changed (a remote edit, a row / column op, a fragment joined late): re-read the rows in
  // view — never under an open cell editor (retried once it closes).
  const version = session?.version ?? 0
  const ready = session?.ready ?? false
  useEffect(() => {
    if (!ready) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const tick = () => {
      const api = gridRef.current?.api
      if (!api) return
      if (api.getEditingCells().length) { timer = setTimeout(tick, REFRESH_MS * 3); return }
      api.refreshInfiniteCache()
    }
    timer = setTimeout(tick, REFRESH_MS)
    return () => { if (timer) clearTimeout(timer) }
  }, [version, ready, gridRef])

  // "Saving…" / a swap: an open cell editor is cancelled (its value would be refused).
  const saving = session?.saving ?? false
  useEffect(() => { if (saving || swapping) gridRef.current?.api?.stopEditing(true) }, [saving, swapping, gridRef])

  // ── save as a copy ──
  const [copyBusy, setCopyBusy] = useState(false)
  const [copyMessage, setCopyMessage] = useState<{ text: string; retry: boolean } | null>(null)
  const lastCopy = useRef<string | undefined>(undefined)
  const [hiddenStale, setHiddenStale] = useState<Set<string>>(new Set())

  const saveCopy = useCallback(async (fromBase?: string) => {
    lastCopy.current = fromBase
    setCopyBusy(true)
    setCopyMessage(null)
    const r: SaveCopyResult = await saveLargeCsvCopy(fileId, parentId, copyName(fileName ?? 'data.csv', t('largeCsv.edit.copySuffix')), fromBase)
    setCopyBusy(false)
    if (r.kind === 'saved') {
      const text = t('largeCsv.edit.copySaved', { name: r.name })
      setCopyMessage({ text, retry: false })
      toast?.success(text)
    } else if (r.kind === 'retry') setCopyMessage({ text: t('largeCsv.edit.copyRetry'), retry: true })
    else if (r.kind === 'gone') {
      setCopyMessage({ text: t('largeCsv.edit.discardFailed.gone'), retry: false })
      if (fromBase) setHiddenStale((h) => new Set(h).add(fromBase))
    } else if (r.kind === 'unencodable') setCopyMessage({ text: t('largeCsv.edit.copyUnencodable'), retry: false })
    else if (r.kind === 'layoutChanged') setCopyMessage({ text: t('largeCsv.edit.layoutChanged'), retry: false })
    else if (r.kind === 'damaged') setCopyMessage({ text: r.message, retry: false })
    else setCopyMessage({ text: t('largeCsv.edit.copyFailed'), retry: false })
  }, [fileId, parentId, fileName, t, toast])

  const retryCopy = useCallback(() => { void saveCopy(lastCopy.current) }, [saveCopy])

  const discardStale = useCallback((base: string) => {
    const s = sessionRef.current
    if (!s) return
    setHiddenStale((h) => new Set(h).add(base))
    s.discard(base)
  }, [])
  // A discard refused: the stale edits are still there — show them again.
  const discardFailedNonce = session?.notice?.kind === 'discard_failed' ? session.notice.nonce : null
  useEffect(() => { if (discardFailedNonce !== null) setHiddenStale(new Set()) }, [discardFailedNonce])

  const staleBases = session ? (summary.staleEdits ?? []).map((e) => e.base).filter((b) => !hiddenStale.has(b)) : []

  return {
    canEdit,
    canSave,
    canEditRef,
    message,
    setCellValue,
    insertRows,
    deleteRow,
    columnOp,
    viewActive,
    setViewActive,
    viewStale: viewStale || provisional,
    showProvisionalView,
    setProvisionalView,
    refreshView,
    copyBusy,
    copyMessage,
    saveCopy: () => void saveCopy(),
    retryCopy,
    staleBases,
    saveStaleCopy: (base: string): Promise<void> => saveCopy(base),
    discardStale,
  }
}
